import { useEffect, useState } from 'react';
import type { AppConfig } from '@devfactory/core';
import { ThemeProvider } from './ThemeProvider';
import { Sidebar, Header, MobileNav } from './Navigation';
import { PageRenderer, PageNotFound } from './PageRenderer';

// =============================================================================
// APP RENDERER
// =============================================================================

interface AppRendererProps {
  config: AppConfig;
}

export function AppRenderer({ config }: AppRendererProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState(() => {
    // Get initial path from URL
    const path = window.location.pathname;
    // Remove app prefix if present
    const match = path.match(/^\/apps\/[^/]+(.*)$/);
    return match ? match[1] || '/' : path || '/';
  });

  // Find current page based on path
  const currentPage = config.pages.find((p) => p.path === currentPath) || config.pages[0];

  // Handle browser back/forward
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      const match = path.match(/^\/apps\/[^/]+(.*)$/);
      setCurrentPath(match ? match[1] || '/' : path || '/');
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handleNavigate = (path: string) => {
    setCurrentPath(path);
    // Update URL without full page reload
    const appPrefix = window.location.pathname.match(/^\/apps\/[^/]+/)?.[0] || '';
    window.history.pushState({}, '', `${appPrefix}${path}`);
    setMobileMenuOpen(false);
  };

  // Default theme if not provided
  const theme = config.theme || {
    primaryColor: '#3B82F6',
    mode: 'light',
    borderRadius: 'md',
  };

  // Default navigation if not provided
  const navigation = config.navigation || {
    items: config.pages.map((page) => ({
      label: page.name,
      path: page.path,
      icon: 'document',
    })),
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="h-screen flex bg-gray-50 dark:bg-gray-900">
        {/* Desktop Sidebar */}
        <div className="hidden lg:flex">
          <Sidebar
            navigation={navigation}
            appName={config.name}
            currentPath={currentPath}
            onNavigate={handleNavigate}
            collapsed={sidebarCollapsed}
            onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          />
        </div>

        {/* Mobile Navigation */}
        <MobileNav
          navigation={navigation}
          appName={config.name}
          currentPath={currentPath}
          onNavigate={handleNavigate}
          isOpen={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
        />

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <Header
            appName={config.name}
            theme={theme}
            onMenuToggle={() => setMobileMenuOpen(true)}
          />

          {/* Page Content */}
          <main className="flex-1 overflow-y-auto">
            {currentPage ? (
              <PageRenderer page={currentPage} forms={config.forms} />
            ) : (
              <PageNotFound />
            )}
          </main>
        </div>
      </div>
    </ThemeProvider>
  );
}

// =============================================================================
// STANDALONE APP SHELL (for apps without config)
// =============================================================================

interface StandaloneAppProps {
  children: React.ReactNode;
  title?: string;
}

export function StandaloneApp({ children, title = 'Application' }: StandaloneAppProps) {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <header className="bg-white dark:bg-gray-800 shadow">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            {title}
          </h1>
        </div>
      </header>
      <main className="mx-auto max-w-7xl py-6 sm:px-6 lg:px-8">{children}</main>
    </div>
  );
}

// =============================================================================
// ERROR BOUNDARY
// =============================================================================

interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary?: () => void;
}

export function ErrorFallback({ error, resetErrorBoundary }: ErrorFallbackProps) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 text-red-500">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="mt-4 text-xl font-semibold text-gray-900 dark:text-white">
            Something went wrong
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{error.message}</p>
          {resetErrorBoundary && (
            <button
              onClick={resetErrorBoundary}
              className="mt-6 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              Try again
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
